import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { MEMBER_PHOTO_TYPES, removeMyPhoto, uploadMyPhoto } from '@/lib/me-api';
import { PhotoCropDialog } from '@/components/team/PhotoCropDialog';

/**
 * Upload / remove your own photo. Render `photoUi` once; `choose()` opens the file picker, then
 * a dialog to pan and zoom the photo before it is saved.
 * Saved photos show straight away in the top bar (see lib/me-api.ts).
 */
export function useMemberPhotoActions(onChanged?: () => void) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  /** Object URL of the photo being positioned. */
  const [cropUrl, setCropUrl] = useState<string | null>(null);

  const closeCrop = () => {
    if (cropUrl) URL.revokeObjectURL(cropUrl);
    setCropUrl(null);
  };

  const upload = async (photo: Blob) => {
    setBusy(true);
    const res = await uploadMyPhoto(photo);
    setBusy(false);
    if (res.error || !res.data) {
      toast.error(res.error ?? 'The photo was not saved');
      return;
    }
    closeCrop();
    onChanged?.();
  };

  const remove = async () => {
    setBusy(true);
    const res = await removeMyPhoto();
    setBusy(false);
    if (res.error || !res.data) {
      toast.error(res.error ?? 'The photo was not removed');
      return;
    }
    onChanged?.();
  };

  const photoUi = (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={MEMBER_PHOTO_TYPES}
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) setCropUrl(URL.createObjectURL(file));
        }}
      />
      <PhotoCropDialog imageUrl={cropUrl} saving={busy} onCancel={closeCrop} onSave={(photo) => void upload(photo)} />
    </>
  );

  return { busy, choose: () => inputRef.current?.click(), remove, photoUi };
}
